import { ref, computed, UnwrapRef, Ref } from 'vue';
import { defineStore } from 'pinia';
import router from '@/router';
import { getKindeClient, getTokenWithRefresh, getTokenExpiration } from '@/utils/kindeAuth';
import { GetOrganizationResponse, ListUserOrganizationsResponse, Organization, UserOrganizationDto } from '@/api-client/models';
import { ApiClientFactory } from '@/stores/apiClientFactory';
import { handleGlobalError } from '@/composables/globalErrorHandler';
import { get } from 'http';

export const useAuthStore = defineStore('auth', () => {
  // State
  const isAuthenticated = ref(false);
  const user = ref<any | null>(null);

  const organizations = ref<Array<UserOrganizationDto>>([]);
  // Orgs the user can switch to in the web admin — the API returns every membership
  // with per-org roles and leaves role filtering to each consumer
  const adminOrganizations = computed(() => organizations.value.filter((org) => org.roles?.includes('admin')));
  const organization = ref<Organization>({});
  // The organization's type gates the agency features (Vendors, vendor codes on products): an
  // Agency represents manufacturers as vendors; a Supplier (the default, also what a missing type
  // reads as) never sees them. Read off the organization document, so the sidebar, the routes and
  // the pages agree the moment it loads (CU-86bcwymr8).
  const isAgency = computed(() => organization.value?.type === 'Agency' || (organization.value as any)?.additionalData?.isAgency === true);
  const roles = ref<Array<string>>([]);
  const admin = ref(false);
  const accessToken: Ref<UnwrapRef<string>> = ref('');
  const currentOrgId = ref('');
  const currentOrg = ref('');
  const priceTiers = <any>ref(null);
  const tiersLoading = ref(false);

  const productAttributes = ref<Array<{displayName?: string | null, slug?: string | null}>>([]);
  const productAttributesLoading = ref(false);
  const customerAttributes = ref<Array<{displayName?: string | null, slug?: string | null}>>([]);
  const customerAttributesLoading = ref(false);
  const orderTypes = ref<Array<{displayName?: string | null, slug?: string | null}>>([]);
  const orderTypesLoading = ref(false);
  // The org's registry of every MarketCode ever set on a rep — offered as suggestions when editing
  // a rep's code. Grows only via admin rep setup (never from codes typed on a cart in the sales app).
  const marketCodes = ref<Array<string>>([]);
  const marketCodesLoading = ref(false);

  const clientFactory = ApiClientFactory.getInstance();
  const client = clientFactory.getClient();

  const logoUrl = ref('')


  // Actions
  const checkAuth = async () => {
    try {
      const kindeClient = getKindeClient();

      // Check if auth data exists in localStorage
      const storedUser = localStorage.getItem('auth_user');
      const storedToken = localStorage.getItem('auth_token');
      const storedOrgId = localStorage.getItem('auth_org_id');
      const storedOrg = localStorage.getItem('auth_org');
      const storedAdmin = localStorage.getItem('auth_admin');

      if (storedUser && storedToken && storedUser !== 'undefined' && storedToken !== 'undefined') {
        user.value = JSON.parse(storedUser);
        accessToken.value = storedToken;
        currentOrgId.value = storedOrgId || '';
        currentOrg.value = storedOrg || '';
        admin.value = storedAdmin === 'true';
        isAuthenticated.value = true;
        return;
      }

      // Fetch user information if not in localStorage
      const fetchedUser = await kindeClient.getUser();
      user.value = fetchedUser;
      isAuthenticated.value = !!fetchedUser;

      accessToken.value = <string>await getTokenWithRefresh();
      currentOrgId.value = <string>kindeClient.getClaim("org_code")?.value || '';
      currentOrg.value = <string>kindeClient.getClaim("org_name")?.value || '';
      const roles: any = kindeClient.getClaim("roles")?.value;

      admin.value = roles?.some((role: any) => role.key === 'admin') || false;

      // Persist auth data to localStorage
      localStorage.setItem('auth_user', JSON.stringify(fetchedUser));
      localStorage.setItem('auth_token', accessToken.value);
      localStorage.setItem('auth_org_id', currentOrgId.value);
      localStorage.setItem('auth_org', currentOrg.value);
      localStorage.setItem('auth_admin', admin.value.toString());

    } catch (error) {
      console.error('[authStore] Authentication error:', error);
      clearAuthData();
    }
  };

  const login = async () => {
    const kindeClient = getKindeClient();
    // Start the login flow (correct method for PKCE SDK)
    await kindeClient.login({
      app_state: {
        redirectTo: window.location
      }
    }); // Correct method
  };

  const logout = async () => {

    clearAuthData();
    try {
      const kindeClient = getKindeClient();
      await kindeClient.logout();
    } catch (error) {
      alert('An error occurred during logout. You will be redirected to the homepage.');
      console.error('Error during logout:', error);
      await router.push('/');
    }
  };

  // Clears the session from both the store and localStorage. Most callers redirect away
  // immediately, which would discard the in-memory state anyway — but not all of them:
  // ApiClientFactory's auth-error handler and the failed-logout fallback both router.push('/')
  // without a reload, and checkAuth's own error path does not navigate at all. On those paths
  // anything left set here survives, so the store would read isAuthenticated: false alongside
  // admin: true and the previous organization's name and logo.
  const clearAuthData = () => {
    isAuthenticated.value = false;
    user.value = null;
    accessToken.value = '';
    admin.value = false;
    currentOrgId.value = '';
    currentOrg.value = '';
    organization.value = {};
    organizations.value = [];
    logoUrl.value = '';
    localStorage.removeItem('auth_user');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_org_id');
    localStorage.removeItem('auth_org');
    localStorage.removeItem('auth_admin');
  }
    
  // Handle the redirect callback after login
  const handleRedirectCallback = async () => {
    try {
      const kindeClient = getKindeClient();
      // Parse the redirect to extract the user data and tokens
      const response = await kindeClient.handleRedirectCallback();

      if (response.user) {
        user.value = response.user;
        isAuthenticated.value = true;
      }
    } catch (error) {
      console.error('Error handling redirect callback:', error);
    }
  };

  // force refresh access token
  const refreshAccessToken = async () => {
    try {
 
      accessToken.value = '';
      // Use the proper refresh utility instead of direct Kinde client call
      const token = await getTokenWithRefresh();
      if (token) {
 
        accessToken.value = token;
        // Update localStorage with new token
        localStorage.setItem('auth_token', token);
      }
    } catch (error) {
      console.error('Error refreshing access token:', error);
    }
  };

  // Getters
  const getUser = computed(() => user.value);

  const getAuthorizationToken = async () => {
    try {

      // only get a new token if our token is expired or about to expire in the next minute
      if (accessToken.value) {
        const expiresAt = getTokenExpiration(accessToken.value);
        if (expiresAt) {
          const now = Math.floor(Date.now() / 1000);
          const REFRESH_THRESHOLD = 3000; // Same threshold as in kindeAuth.ts
          const shouldRefresh = expiresAt - now < REFRESH_THRESHOLD;
          
          if (!shouldRefresh) { 
       
            return accessToken.value;
          }
        }
      }
      

      // Get fresh token with automatic refresh if needed
      const token = await getTokenWithRefresh();
      if (token) {
        accessToken.value = token;
        // Update localStorage with new token
        localStorage.setItem('auth_token', token);
        // Note: Feature flags will be automatically updated by the useFeatureFlags composable
        // when it detects the token change via the auth store watcher
      }
    
      return accessToken.value;
    } catch (error) {
      console.error('Error getting authorization token:', error);
      // If token refresh fails, trigger login flow
      if (!accessToken.value) {
        clearAuthData();
        await login();
      }
      return accessToken.value;
    }
  };

  const getPortalSessionLink = async () => {
    try {
      const response = await client.api.organizations.subscriptions.portalSessionLink.get();
      return response;
    } catch (error: any) {
      handleGlobalError(error);
    }
  };

  const getReadmeLoginUrl = async () => {
    try {
      const response = await client.api.auth.readme.get();
      return response;
    } catch (error: any) {
      handleGlobalError(error);
    }
};

// Raised when a switch cannot even be attempted. `name` is what handleGlobalError
// shows as the title, so it reads as a sentence rather than "Error".
const switchOrganizationError = (message: string) => {
  const error = new Error(message);
  error.name = 'Cannot switch organization';
  return error;
};

// Switch the active organization by re-authenticating with Kinde so all org
// claims (org_code, org_name, OrganizationId) are re-minted for the target org.
// Redirects away; AuthCallback lands on /dashboard afterwards.
//
// Returns true when a switch was started and false when the requested org is
// already the active one — a real no-op, not a failure. Throws when the switch
// cannot be attempted at all. These were previously one bare `return`, which
// left any button wired to this completely inert with nothing on screen or in
// the console to say why (CU-86bbjq1ag, and CU-86b3hk7uh before it).
const switchOrganization = async (orgCode: string): Promise<boolean> => {
  if (!orgCode) {
    throw switchOrganizationError('No organization code was provided, so there is nothing to switch to.');
  }

  if (orgCode === currentOrgId.value) return false;

  const kindeClient = getKindeClient();
  if (!kindeClient) {
    throw switchOrganizationError('The authentication client is not ready yet. Please refresh and try again.');
  }

  // checkAuth() short-circuits on these cached keys, so they must go before the
  // redirect or the app comes back still showing the previous org
  clearAuthData();
  await kindeClient.login({ org_code: orgCode });
  return true;
};

const getOrganization = async () => {
  try {
      const response = await client.api.organization.get();
      organization.value = response?.organization ?? {};
      return organization.value;
    } catch (error: any) {
      handleGlobalError(error);
    }
  };


const getLogoUrl = async () => {
  try {
      const response = await client.api.organization.get();
      logoUrl.value = response?.organization?.logoUrl ?? '';
      organization.value = response?.organization ?? {};

    } catch (error: any) {
      handleGlobalError(error);
    }
  };

  const getOrganizations = async () => {
    try {
      const response: ListUserOrganizationsResponse | undefined = await client.api.users.organizations.get();
      organizations.value = response?.organizations ?? [];
      return organizations.value;
    } catch (error: any) {
      handleGlobalError(error);
    }
  };

  const getTerms = async () => {
    // check if organization has terms
    if (!organization.value) {
      await getOrganization();
    }
    return organization.value?.terms ?? [];
  };

  const getPriceTiers = async () => {
    tiersLoading.value = true;
    try {
      const response = await client.api.organization.get();
      priceTiers.value = response?.organization?.priceTiers;
    } catch (error: any) {
      handleGlobalError(error);
    } finally {
      tiersLoading.value = false;
    }
  };

  const getProductAttributes = async () => {
    productAttributesLoading.value = true;
    try {
      const response = await getOrganization();
      productAttributes.value = organization.value?.productAttributes ?? [];
      productAttributesLoading.value = false;
    } catch (error: any) {
      handleGlobalError(error);
    } finally {
      productAttributesLoading.value = false;
    }
  };

  const getCustomerAttributes = async () => {
    customerAttributesLoading.value = true;
    try {
      const response = await getOrganization();
      customerAttributes.value = organization.value?.customerAttributes ?? [];
      customerAttributesLoading.value = false;
    } catch (error: any) {
      handleGlobalError(error);
    } finally {
      customerAttributesLoading.value = false;
    }
  };

  const getOrderTypes = async () => {
    orderTypesLoading.value = true;
    try {
      const response = await getOrganization();
      orderTypes.value = organization.value?.orderTypes ?? [];
      orderTypesLoading.value = false;
    } catch (error: any) {
      handleGlobalError(error);
    } finally {
      orderTypesLoading.value = false;
    }
  };

  const getMarketCodes = async () => {
    marketCodesLoading.value = true;
    try {
      await getOrganization();
      marketCodes.value = organization.value?.marketCodes ?? [];
    } catch (error: any) {
      handleGlobalError(error);
    } finally {
      marketCodesLoading.value = false;
    }
  };

  return {
    isAuthenticated,
    user,
    admin,
    accessToken,
    checkAuth,
    login,
    logout,
    handleRedirectCallback,
    getUser,
    getAuthorizationToken,
    getPortalSessionLink,
    getReadmeLoginUrl,
    switchOrganization,
    getOrganizations,
    organizations,
    adminOrganizations,
    getLogoUrl,
    getPriceTiers,
    getTerms,
    getProductAttributes,
    currentOrg,
    currentOrgId,
    priceTiers,
    tiersLoading,
    logoUrl,
    getOrganization,
    organization,
    isAgency,
    getCustomerAttributes,
    customerAttributes,
    customerAttributesLoading,
    getOrderTypes,
    orderTypes,
    orderTypesLoading,
    getMarketCodes,
    marketCodes,
    marketCodesLoading,
    clearAuthData,
    refreshAccessToken
  };
});
